import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { stageDetails } from '../data/quizData';

const { FiRefreshCw, FiDownload, FiShare2, FiHeart, FiTrendingUp, FiStar } = FiIcons;

const Results = ({ quizData }) => {
  const { stage, score, completedAt } = quizData;
  const stageInfo = stageDetails[stage];

  if (!stage) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No quiz results found.</p>
          <Link to="/" className="text-purple-600 hover:text-purple-700">
            Take the quiz
          </Link>
        </div>
      </div>
    );
  }

  const getStageColor = (stage) => {
    const colors = {
      'raw-pain': 'from-red-400 to-red-600',
      'anger-bargaining': 'from-orange-400 to-orange-600',
      'processing': 'from-yellow-400 to-yellow-600',
      'rebuilding': 'from-blue-400 to-blue-600',
      'thriving': 'from-green-400 to-green-600'
    };
    return colors[stage] || 'from-purple-400 to-purple-600';
  };

  const downloadResults = () => {
    const results = `
BREAKUP RECOVERY QUIZ RESULTS
Generated on: ${new Date(completedAt).toLocaleDateString()}

Your Stage: ${stageInfo.title}
Score: ${score}/100

${stageInfo.description}

Key Characteristics:
${stageInfo.characteristics.map(char => `• ${char}`).join('\n')}

Recommended Actions:
${stageInfo.recommendations.map(rec => `• ${rec}`).join('\n')}

Next Steps:
${stageInfo.nextSteps.map(step => `• ${step}`).join('\n')}
    `;

    const blob = new Blob([results], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'breakup-recovery-results.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareResults = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My Breakup Recovery Results',
        text: `I'm in the "${stageInfo.title}" stage of breakup recovery. Take the quiz to find out where you are!`,
        url: window.location.origin
      });
    }
  };

  return (
    <div className="min-h-screen p-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${getStageColor(stage)} rounded-full mb-4`}>
            <SafeIcon icon={FiHeart} className="text-3xl text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Your Results</h1>
          <p className="text-gray-600">Completed on {new Date(completedAt).toLocaleDateString()}</p>
        </motion.div>

        {/* Main Results Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <div className="text-center mb-8">
            <div className={`inline-block px-6 py-2 bg-gradient-to-r ${getStageColor(stage)} text-white rounded-full text-lg font-semibold mb-4`}>
              Stage {stageInfo.number}: {stageInfo.title}
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-2">
              Recovery Score: {score}/100
            </div>
            <p className="text-xl text-gray-600">{stageInfo.description}</p>
          </div>

          {/* Progress Visualization */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Recovery Journey</h3>
            <div className="relative">
              <div className="flex justify-between items-center mb-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div
                    key={num}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      num <= stageInfo.number
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {num}
                  </div>
                ))}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(stageInfo.number / 5) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Key Characteristics */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiStar} className="mr-2 text-purple-600" />
                Key Characteristics
              </h3>
              <ul className="space-y-2">
                {stageInfo.characteristics.map((char, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                    className="flex items-start"
                  >
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{char}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <SafeIcon icon={FiTrendingUp} className="mr-2 text-purple-600" />
                Recommended Actions
              </h3>
              <ul className="space-y-2">
                {stageInfo.recommendations.map((rec, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                    className="flex items-start"
                  >
                    <div className="w-2 h-2 bg-pink-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{rec}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Next Steps</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {stageInfo.nextSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.3 }}
                  className="bg-white rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-center mb-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-800">Step {index + 1}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{step}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={downloadResults}
              className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all duration-200"
            >
              <SafeIcon icon={FiDownload} className="mr-2" />
              Download Results
            </button>
            <button
              onClick={shareResults}
              className="inline-flex items-center px-6 py-3 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-all duration-200"
            >
              <SafeIcon icon={FiShare2} className="mr-2" />
              Share Results
            </button>
            <Link to="/">
              <button className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                <SafeIcon icon={FiRefreshCw} className="mr-2" />
                Retake Quiz
              </button>
            </Link>
          </div>
        </motion.div>

        {/* Encouragement Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-xl p-8 text-white text-center"
        >
          <SafeIcon icon={FiHeart} className="text-4xl mb-4 mx-auto" />
          <h2 className="text-2xl font-bold mb-4">Remember</h2>
          <p className="text-lg opacity-90 mb-4">
            Healing isn't linear, and every stage is valid. You're exactly where you need to be right now.
          </p>
          <p className="opacity-80">
            Be patient with yourself and celebrate the small victories along the way. 💜
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Results;